/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { EmployeeType, TimeType, UserRole } from 'src/graphql/generated/graphql.generated';
import {
  UserApplicationSettingsRecord,
  UserApplicationSettingsProps,
} from 'src/store/generated/user-application-settings-record.generated';
import {
  IdListItemRecord,
  IdListItemProps,
} from 'src/store/generated/id-list-item-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface PersonProps {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  title: string | null;
  employeeId: string | null;
  employeeType: EmployeeType;
  timeType: TimeType;
  hireDate: Date | null;
  terminationDate: Date | null;
  departmentId: string | null;
  reportsToPersonId: string | null;
  baseId: string | null;
  locationId: string | null;
  programId: string | null;
  activeDirectoryId: string | null;
  activateOktaAccount: boolean;
  grantTcsAccess: boolean;
  notificationAddress: string | null;
  userRoles: Set<UserRole> | null;
  allCompaniesAccessible: boolean | null;
  allBasesAccessible: boolean | null;
  allTrmProgramsAccessible: boolean | null;
  allCommunicationCentersAccessible: boolean | null;
  faaCompanyId: string | null;
  dispatchCompanyId: string | null;
  aviationCompanyId: string | null;
  primaryCommunicationCenterId: string | null;
  isOutOfFlightCompliance: boolean;
  outOfFlightComplianceComment: string | null;
  dateLastUpdatedFromGMRWorkday: Date | null;
  appSettings: UserApplicationSettingsProps | null;
  additionalCompanies: List<IdListItemProps> | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class PersonRecord extends Record<PersonProps>({
  id: null as any,
  firstName: null as any,
  lastName: null as any,
  email: null as any,
  title: null as any,
  employeeId: null as any,
  employeeType: null as any,
  timeType: null as any,
  hireDate: null as any,
  terminationDate: null as any,
  departmentId: null as any,
  reportsToPersonId: null as any,
  baseId: null as any,
  locationId: null as any,
  programId: null as any,
  activeDirectoryId: null as any,
  activateOktaAccount: null as any,
  grantTcsAccess: null as any,
  notificationAddress: null as any,
  userRoles: null as any,
  allCompaniesAccessible: null as any,
  allBasesAccessible: null as any,
  allTrmProgramsAccessible: null as any,
  allCommunicationCentersAccessible: null as any,
  faaCompanyId: null as any,
  dispatchCompanyId: null as any,
  aviationCompanyId: null as any,
  primaryCommunicationCenterId: null as any,
  isOutOfFlightCompliance: null as any,
  outOfFlightComplianceComment: null as any,
  dateLastUpdatedFromGMRWorkday: null as any,
  appSettings: null as any,
  additionalCompanies: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    title: string | null;
    employeeId: string | null;
    employeeType: EmployeeType;
    timeType: TimeType;
    hireDate: string | number | Date | null;
    terminationDate: string | number | Date | null;
    departmentId: string | null;
    reportsToPersonId: string | null;
    baseId: string | null;
    locationId: string | null;
    programId: string | null;
    activeDirectoryId: string | null;
    activateOktaAccount: boolean;
    grantTcsAccess: boolean;
    notificationAddress: string | null;
    userRoles: any;
    allCompaniesAccessible: boolean | null;
    allBasesAccessible: boolean | null;
    allTrmProgramsAccessible: boolean | null;
    allCommunicationCentersAccessible: boolean | null;
    faaCompanyId: string | null;
    dispatchCompanyId: string | null;
    aviationCompanyId: string | null;
    primaryCommunicationCenterId: string | null;
    isOutOfFlightCompliance: boolean;
    outOfFlightComplianceComment: string | null;
    dateLastUpdatedFromGMRWorkday: string | number | Date | null;
    appSettings: any;
    additionalCompanies: any;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): PersonRecord {
    if (json == null) {
      throw new Error('PersonRecord.fromJS: parameter cannot be null');
    }
    return new PersonRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'PersonJson.id',
      ),
      firstName: getFromOrThrow(json, (j) => j.firstName, 'PersonJson.firstName'),
      lastName: getFromOrThrow(json, (j) => j.lastName, 'PersonJson.lastName'),
      email: getFromOrThrow(json, (j) => j.email, 'PersonJson.email'),
      title: json.title,
      employeeId: json.employeeId,
      employeeType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'employeeType', EmployeeType.Unspecified),
        'PersonJson.employeeType',
      ),
      timeType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'timeType', TimeType.Unspecified),
        'PersonJson.timeType',
      ),
      hireDate: json.hireDate != null ? new Date(json.hireDate) : null,
      terminationDate: json.terminationDate != null ? new Date(json.terminationDate) : null,
      departmentId: json.departmentId,
      reportsToPersonId: json.reportsToPersonId,
      baseId: json.baseId,
      locationId: json.locationId,
      programId: json.programId,
      activeDirectoryId: json.activeDirectoryId,
      activateOktaAccount: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'activateOktaAccount', false),
        'PersonJson.activateOktaAccount',
      ),
      grantTcsAccess: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'grantTcsAccess', false),
        'PersonJson.grantTcsAccess',
      ),
      notificationAddress: json.notificationAddress,
      userRoles: json.userRoles != null ? Set<UserRole>(json.userRoles) : null,
      allCompaniesAccessible: json.allCompaniesAccessible,
      allBasesAccessible: json.allBasesAccessible,
      allTrmProgramsAccessible: json.allTrmProgramsAccessible,
      allCommunicationCentersAccessible: json.allCommunicationCentersAccessible,
      faaCompanyId: json.faaCompanyId,
      dispatchCompanyId: json.dispatchCompanyId,
      aviationCompanyId: json.aviationCompanyId,
      primaryCommunicationCenterId: json.primaryCommunicationCenterId,
      isOutOfFlightCompliance: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isOutOfFlightCompliance', false),
        'PersonJson.isOutOfFlightCompliance',
      ),
      outOfFlightComplianceComment: json.outOfFlightComplianceComment,
      dateLastUpdatedFromGMRWorkday:
        json.dateLastUpdatedFromGMRWorkday != null
          ? new Date(json.dateLastUpdatedFromGMRWorkday)
          : null,
      appSettings:
        json.appSettings != null ? new UserApplicationSettingsRecord(json.appSettings) : null,
      additionalCompanies:
        json.additionalCompanies != null
          ? List<IdListItemRecord>(
              json.additionalCompanies.map((o: any) => new IdListItemRecord(o)),
            )
          : null,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'PersonJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    title: string | null;
    employeeId: string | null;
    employeeType: EmployeeType;
    timeType: TimeType;
    hireDate: string | number | Date | null;
    terminationDate: string | number | Date | null;
    departmentId: string | null;
    reportsToPersonId: string | null;
    baseId: string | null;
    locationId: string | null;
    programId: string | null;
    activeDirectoryId: string | null;
    activateOktaAccount: boolean;
    grantTcsAccess: boolean;
    notificationAddress: string | null;
    userRoles: any;
    allCompaniesAccessible: boolean | null;
    allBasesAccessible: boolean | null;
    allTrmProgramsAccessible: boolean | null;
    allCommunicationCentersAccessible: boolean | null;
    faaCompanyId: string | null;
    dispatchCompanyId: string | null;
    aviationCompanyId: string | null;
    primaryCommunicationCenterId: string | null;
    isOutOfFlightCompliance: boolean;
    outOfFlightComplianceComment: string | null;
    dateLastUpdatedFromGMRWorkday: string | number | Date | null;
    appSettings: any;
    additionalCompanies: any;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [PersonRecord, Error[]] {
    if (json == null) {
      throw new Error('PersonRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new PersonRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'PersonJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      firstName: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.firstName, 'PersonJson.firstName'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastName: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.lastName, 'PersonJson.lastName'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      email: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.email, 'PersonJson.email'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      title: tryOrDefault(
        () => json.title,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      employeeId: tryOrDefault(
        () => json.employeeId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      employeeType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'employeeType', EmployeeType.Unspecified),
            'PersonJson.employeeType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      timeType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'timeType', TimeType.Unspecified),
            'PersonJson.timeType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      hireDate: tryOrDefault(
        () => (json.hireDate != null ? new Date(json.hireDate) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      terminationDate: tryOrDefault(
        () => (json.terminationDate != null ? new Date(json.terminationDate) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      departmentId: tryOrDefault(
        () => json.departmentId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      reportsToPersonId: tryOrDefault(
        () => json.reportsToPersonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      baseId: tryOrDefault(
        () => json.baseId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      locationId: tryOrDefault(
        () => json.locationId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      programId: tryOrDefault(
        () => json.programId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      activeDirectoryId: tryOrDefault(
        () => json.activeDirectoryId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      activateOktaAccount: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'activateOktaAccount', false),
            'PersonJson.activateOktaAccount',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      grantTcsAccess: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'grantTcsAccess', false),
            'PersonJson.grantTcsAccess',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notificationAddress: tryOrDefault(
        () => json.notificationAddress,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      userRoles: tryOrDefault(
        () => (json.userRoles != null ? Set<UserRole>(json.userRoles) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      allCompaniesAccessible: tryOrDefault(
        () => json.allCompaniesAccessible,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      allBasesAccessible: tryOrDefault(
        () => json.allBasesAccessible,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      allTrmProgramsAccessible: tryOrDefault(
        () => json.allTrmProgramsAccessible,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      allCommunicationCentersAccessible: tryOrDefault(
        () => json.allCommunicationCentersAccessible,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      faaCompanyId: tryOrDefault(
        () => json.faaCompanyId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dispatchCompanyId: tryOrDefault(
        () => json.dispatchCompanyId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      aviationCompanyId: tryOrDefault(
        () => json.aviationCompanyId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      primaryCommunicationCenterId: tryOrDefault(
        () => json.primaryCommunicationCenterId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isOutOfFlightCompliance: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isOutOfFlightCompliance', false),
            'PersonJson.isOutOfFlightCompliance',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      outOfFlightComplianceComment: tryOrDefault(
        () => json.outOfFlightComplianceComment,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateLastUpdatedFromGMRWorkday: tryOrDefault(
        () =>
          json.dateLastUpdatedFromGMRWorkday != null
            ? new Date(json.dateLastUpdatedFromGMRWorkday)
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      appSettings: tryOrDefault(
        () =>
          json.appSettings != null ? new UserApplicationSettingsRecord(json.appSettings) : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      additionalCompanies: tryOrDefault(
        () =>
          json.additionalCompanies != null
            ? List<IdListItemRecord>(
                json.additionalCompanies.map((o: any) => new IdListItemRecord(o)),
              )
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'PersonJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: PersonProps) {
    super(props);
    if (props == null) {
      throw new Error('PersonRecord constructor parameter cannot be null');
    }
  }
}

export abstract class PersonViewModelBase implements PersonProps {
  protected readonly _props: PersonProps;
  private _json?: PersonProps;

  get id(): string {
    return this._props.id;
  }
  get firstName(): string {
    return this._props.firstName;
  }
  get lastName(): string {
    return this._props.lastName;
  }
  get email(): string {
    return this._props.email;
  }
  get title(): string | null {
    return this._props.title;
  }
  get employeeId(): string | null {
    return this._props.employeeId;
  }
  get employeeType(): EmployeeType {
    return this._props.employeeType;
  }
  get timeType(): TimeType {
    return this._props.timeType;
  }
  get hireDate(): Date | null {
    return this._props.hireDate;
  }
  get terminationDate(): Date | null {
    return this._props.terminationDate;
  }
  get departmentId(): string | null {
    return this._props.departmentId;
  }
  get reportsToPersonId(): string | null {
    return this._props.reportsToPersonId;
  }
  get baseId(): string | null {
    return this._props.baseId;
  }
  get locationId(): string | null {
    return this._props.locationId;
  }
  get programId(): string | null {
    return this._props.programId;
  }
  get activeDirectoryId(): string | null {
    return this._props.activeDirectoryId;
  }
  get activateOktaAccount(): boolean {
    return this._props.activateOktaAccount;
  }
  get grantTcsAccess(): boolean {
    return this._props.grantTcsAccess;
  }
  get notificationAddress(): string | null {
    return this._props.notificationAddress;
  }
  get userRoles(): Set<UserRole> | null {
    return this._props.userRoles;
  }
  get allCompaniesAccessible(): boolean | null {
    return this._props.allCompaniesAccessible;
  }
  get allBasesAccessible(): boolean | null {
    return this._props.allBasesAccessible;
  }
  get allTrmProgramsAccessible(): boolean | null {
    return this._props.allTrmProgramsAccessible;
  }
  get allCommunicationCentersAccessible(): boolean | null {
    return this._props.allCommunicationCentersAccessible;
  }
  get faaCompanyId(): string | null {
    return this._props.faaCompanyId;
  }
  get dispatchCompanyId(): string | null {
    return this._props.dispatchCompanyId;
  }
  get aviationCompanyId(): string | null {
    return this._props.aviationCompanyId;
  }
  get primaryCommunicationCenterId(): string | null {
    return this._props.primaryCommunicationCenterId;
  }
  get isOutOfFlightCompliance(): boolean {
    return this._props.isOutOfFlightCompliance;
  }
  get outOfFlightComplianceComment(): string | null {
    return this._props.outOfFlightComplianceComment;
  }
  get dateLastUpdatedFromGMRWorkday(): Date | null {
    return this._props.dateLastUpdatedFromGMRWorkday;
  }
  get appSettings(): UserApplicationSettingsProps | null {
    return this._props.appSettings;
  }
  get additionalCompanies(): List<IdListItemProps> | null {
    return this._props.additionalCompanies;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isContingent() {
    return this._props.employeeType === EmployeeType.Contingent;
  }
  public get isNonEmployee() {
    return this._props.employeeType === EmployeeType.NonEmployee;
  }
  public get isPrn() {
    return this._props.employeeType === EmployeeType.Prn;
  }
  public get isRegular() {
    return this._props.employeeType === EmployeeType.Regular;
  }
  public get isTemporary() {
    return this._props.employeeType === EmployeeType.Temporary;
  }
  public get isEmployeeTypeUnspecified() {
    return this._props.employeeType === EmployeeType.Unspecified;
  }
  public get isTimeTypeUnspecified() {
    return this._props.timeType === TimeType.Unspecified;
  }
  public get isFullTime() {
    return this._props.timeType === TimeType.FullTime;
  }
  public get isPartTime() {
    return this._props.timeType === TimeType.PartTime;
  }

  public isAnyEmployeeType(...employeeType: EmployeeType[]) {
    return employeeType.includes(this._props.employeeType);
  }
  public isAnyTimeType(...timeType: TimeType[]) {
    return timeType.includes(this._props.timeType);
  }

  constructor(props: PersonProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        firstName: this._props.firstName,
        lastName: this._props.lastName,
        email: this._props.email,
        title: this._props.title,
        employeeId: this._props.employeeId,
        employeeType: this._props.employeeType,
        timeType: this._props.timeType,
        hireDate: this._props.hireDate,
        terminationDate: this._props.terminationDate,
        departmentId: this._props.departmentId,
        reportsToPersonId: this._props.reportsToPersonId,
        baseId: this._props.baseId,
        locationId: this._props.locationId,
        programId: this._props.programId,
        activeDirectoryId: this._props.activeDirectoryId,
        activateOktaAccount: this._props.activateOktaAccount,
        grantTcsAccess: this._props.grantTcsAccess,
        notificationAddress: this._props.notificationAddress,
        userRoles: this._props.userRoles,
        allCompaniesAccessible: this._props.allCompaniesAccessible,
        allBasesAccessible: this._props.allBasesAccessible,
        allTrmProgramsAccessible: this._props.allTrmProgramsAccessible,
        allCommunicationCentersAccessible: this._props.allCommunicationCentersAccessible,
        faaCompanyId: this._props.faaCompanyId,
        dispatchCompanyId: this._props.dispatchCompanyId,
        aviationCompanyId: this._props.aviationCompanyId,
        primaryCommunicationCenterId: this._props.primaryCommunicationCenterId,
        isOutOfFlightCompliance: this._props.isOutOfFlightCompliance,
        outOfFlightComplianceComment: this._props.outOfFlightComplianceComment,
        dateLastUpdatedFromGMRWorkday: this._props.dateLastUpdatedFromGMRWorkday,
        appSettings: this._props.appSettings,
        additionalCompanies: this._props.additionalCompanies,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllPersonEntities = (state: AppState) => state.entityStores.person.entities;

export const selectPersonById = (id: string) => (state: AppState) =>
  state.entityStores.person.entities.get(id);

export const selectPersonMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.person.meta.get(id);

export const selectPersonAndMetadataById = (id: string) =>
  createSelector(selectPersonById(id), selectPersonMetadataById(id), (person, meta) => ({
    person,
    meta,
  }));

export function selectPersonEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllPersonEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, PersonRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectPersonEntitiesByFirstName(firstName: string) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.firstName === firstName).valueSeq(),
  );
}

export function selectPersonEntitiesByLastName(lastName: string) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.lastName === lastName).valueSeq(),
  );
}

export function selectPersonEntitiesByEmail(email: string) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.email === email).valueSeq(),
  );
}

export function selectPersonEntitiesByTitle(title: string) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.title === title).valueSeq(),
  );
}

export function selectPersonEntitiesByEmployeeId(employeeId: string) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.employeeId === employeeId).valueSeq(),
  );
}

export function selectPersonEntitiesByEmployeeType(employeeType: EmployeeType) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.employeeType === employeeType).valueSeq(),
  );
}

export function selectPersonEntitiesByTimeType(timeType: TimeType) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.timeType === timeType).valueSeq(),
  );
}

export function selectPersonEntitiesByHireDate(hireDate: Date) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.hireDate === hireDate).valueSeq(),
  );
}

export function selectPersonEntitiesByTerminationDate(terminationDate: Date) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.terminationDate === terminationDate).valueSeq(),
  );
}

export function selectPersonEntitiesByDepartmentId(departmentId: string) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.departmentId === departmentId).valueSeq(),
  );
}

export function selectPersonEntitiesByReportsToPersonId(reportsToPersonId: string) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.reportsToPersonId === reportsToPersonId).valueSeq(),
  );
}

export function selectPersonEntitiesByBaseId(baseId: string) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.baseId === baseId).valueSeq(),
  );
}

export function selectPersonEntitiesByLocationId(locationId: string) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.locationId === locationId).valueSeq(),
  );
}

export function selectPersonEntitiesByProgramId(programId: string) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.programId === programId).valueSeq(),
  );
}

export function selectPersonEntitiesByActiveDirectoryId(activeDirectoryId: string) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.activeDirectoryId === activeDirectoryId).valueSeq(),
  );
}

export function selectPersonEntitiesByActivateOktaAccount(activateOktaAccount: boolean) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.activateOktaAccount === activateOktaAccount).valueSeq(),
  );
}

export function selectPersonEntitiesByGrantTcsAccess(grantTcsAccess: boolean) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.grantTcsAccess === grantTcsAccess).valueSeq(),
  );
}

export function selectPersonEntitiesByNotificationAddress(notificationAddress: string) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.notificationAddress === notificationAddress).valueSeq(),
  );
}

export function selectPersonEntitiesByAllCompaniesAccessible(allCompaniesAccessible: boolean) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.allCompaniesAccessible === allCompaniesAccessible).valueSeq(),
  );
}

export function selectPersonEntitiesByAllBasesAccessible(allBasesAccessible: boolean) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.allBasesAccessible === allBasesAccessible).valueSeq(),
  );
}

export function selectPersonEntitiesByAllTrmProgramsAccessible(allTrmProgramsAccessible: boolean) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.allTrmProgramsAccessible === allTrmProgramsAccessible).valueSeq(),
  );
}

export function selectPersonEntitiesByAllCommunicationCentersAccessible(
  allCommunicationCentersAccessible: boolean,
) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities
      .filter((e) => e.allCommunicationCentersAccessible === allCommunicationCentersAccessible)
      .valueSeq(),
  );
}

export function selectPersonEntitiesByFaaCompanyId(faaCompanyId: string) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.faaCompanyId === faaCompanyId).valueSeq(),
  );
}

export function selectPersonEntitiesByDispatchCompanyId(dispatchCompanyId: string) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.dispatchCompanyId === dispatchCompanyId).valueSeq(),
  );
}

export function selectPersonEntitiesByAviationCompanyId(aviationCompanyId: string) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.aviationCompanyId === aviationCompanyId).valueSeq(),
  );
}

export function selectPersonEntitiesByPrimaryCommunicationCenterId(
  primaryCommunicationCenterId: string,
) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities
      .filter((e) => e.primaryCommunicationCenterId === primaryCommunicationCenterId)
      .valueSeq(),
  );
}

export function selectPersonEntitiesByIsOutOfFlightCompliance(isOutOfFlightCompliance: boolean) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.isOutOfFlightCompliance === isOutOfFlightCompliance).valueSeq(),
  );
}

export function selectPersonEntitiesByOutOfFlightComplianceComment(
  outOfFlightComplianceComment: string,
) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities
      .filter((e) => e.outOfFlightComplianceComment === outOfFlightComplianceComment)
      .valueSeq(),
  );
}

export function selectPersonEntitiesByDateLastUpdatedFromGMRWorkday(
  dateLastUpdatedFromGMRWorkday: Date,
) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities
      .filter((e) => e.dateLastUpdatedFromGMRWorkday === dateLastUpdatedFromGMRWorkday)
      .valueSeq(),
  );
}

export function selectPersonEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllPersonEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
